/**
 * ChatRoomView — reusable chat pieces shared by the full-page chat
 * (DoserneChat.jsx) and the Messenger-style floating windows (ChatFab.jsx).
 *
 * Exports:
 *   - RoomView            main message list + input area
 *   - MessageBubble       single message with reactions/reply/menu
 *   - MarkToProjectModal  attach a chat photo to project progress
 *   - NewRoomModal        start a DM / group
 *   - helpers: isOnline, timeAgo, fmtTime, otherMember, EMOJI_SET
 *
 * This file is intentionally standalone — it MUST NOT import
 * DoserneShell (that would create a circular import via ChatFab).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Send, Image as ImageIcon, X, Users, Plus, MoreVertical, ArrowLeft,
  MessageCircle, Reply, Tag, Trash2, Search, CornerDownRight, Check,
  Smile, Mic, Play, Pause, Bell, BellOff, Trash, ThumbsUp, Camera,
  Phone, Video, Paperclip, FileText, Images,
} from "lucide-react";
const EmojiPicker = lazy(() => import("emoji-picker-react"));
import { useVoiceRecorder } from "./useVoiceRecorder";
import { useLang } from "./i18n";
import PhotoEditor from "./PhotoEditor";
const CallOverlay = lazy(() => import("./CallOverlay"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 4000;

export const EMOJI_SET = ["👍", "❤️", "🔥", "✅", "😂", "🎉"];

// ---- File attachment helpers (web) ----
function fileExtOf(name = "", type = "") {
  const m = (name.split(".").pop() || "").toLowerCase();
  if (m && m.length <= 5) return m;
  if (type.includes("pdf")) return "pdf";
  if (type.includes("word")) return "docx";
  if (type.includes("sheet") || type.includes("excel")) return "xlsx";
  return "file";
}
function fileMetaOf(name = "", type = "") {
  const e = fileExtOf(name, type);
  if (e === "pdf") return { color: "#dc2626", label: "PDF" };
  if (["doc", "docx"].includes(e)) return { color: "#2563eb", label: "DOC" };
  if (["xls", "xlsx", "csv"].includes(e)) return { color: "#16a34a", label: "XLS" };
  if (["ppt", "pptx"].includes(e)) return { color: "#ea580c", label: "PPT" };
  return { color: "#64748b", label: e.toUpperCase() };
}
function fmtBytes(b = 0) {
  return b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`;
}
// In-app preview URL: office docs via Office Online, everything else via Google gview.
function embedViewerUrl(url = "", name = "", type = "") {
  const e = fileExtOf(name, type);
  if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(e)) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  }
  if (e === "pdf") return url;
  return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;
}

// Full-screen in-app viewer for photos, videos and documents. Nothing opens
// in an external tab — everything stays inside the platform.
export function MediaViewer({ item, onClose }) {
  if (!item) return null;
  const { kind, url, name, ftype } = item;
  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/90 flex flex-col" data-testid="chat-media-viewer">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="truncate font-bold text-sm pr-3">{name || (kind === "video" ? "Video" : kind === "photo" ? "Bilde" : "Fil")}</div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0" data-testid="chat-media-viewer-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center p-2">
        {kind === "photo" ? (
          <img src={url} alt={name || ""} className="max-w-full max-h-full object-contain" />
        ) : kind === "video" ? (
          <video src={url} controls autoPlay className="max-w-full max-h-full" data-testid="chat-media-viewer-video" />
        ) : (
          <iframe title={name || "file"} src={embedViewerUrl(url, name, ftype)} className="w-full h-full bg-white rounded" data-testid="chat-media-viewer-frame" />
        )}
      </div>
    </div>,
    document.body
  );
}

// In-app "Media & files" gallery for a room — all photos, videos and files
// grouped by day. Opens each item in the in-app MediaViewer.
export function MediaGallery({ roomId, authHeader, onClose, onOpen }) {
  const [media, setMedia] = useState(null);
  const [tab, setTab] = useState("media"); // media | files
  useEffect(() => {
    axios.get(`${API}/doserne/chat/rooms/${roomId}/media`, authHeader())
      .then(({ data }) => setMedia(data.media || []))
      .catch(() => setMedia([]));
  }, [roomId, authHeader]);

  const dayLabel = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const same = (a, b) => a.toDateString() === b.toDateString();
    if (same(d, today)) return "I dag";
    if (same(d, yest)) return "I går";
    return d.toLocaleDateString(_localeFor(_currentLang()), { day: "numeric", month: "long", year: "numeric" });
  };

  const list = (media || []).filter((m) => tab === "media" ? (m.photo_url || m.video_url) : m.file_url);
  const groups = [];
  const byDay = {};
  for (const m of list) {
    const k = dayLabel(m.created_at);
    if (!byDay[k]) { byDay[k] = []; groups.push(k); }
    byDay[k].push(m);
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="chat-media-gallery">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-black text-slate-900">Media og filer</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="chat-media-gallery-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1 p-2 border-b border-slate-100">
          <button onClick={() => setTab("media")} className={`flex-1 py-2 rounded-lg text-sm font-bold ${tab === "media" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`} style={tab === "media" ? { color: "#fff" } : {}} data-testid="gallery-tab-media">Media</button>
          <button onClick={() => setTab("files")} className={`flex-1 py-2 rounded-lg text-sm font-bold ${tab === "files" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`} style={tab === "files" ? { color: "#fff" } : {}} data-testid="gallery-tab-files">Filer</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {media === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : list.length === 0 ? (
            <div className="text-center text-sm text-slate-400 italic py-10" data-testid="gallery-empty">{tab === "media" ? "Ingen bilder eller videoer ennå" : "Ingen filer ennå"}</div>
          ) : (
            groups.map((g) => (
              <div key={g} className="mb-4">
                <div className="text-[11px] font-black text-slate-400 uppercase mb-2">{g}</div>
                {tab === "media" ? (
                  <div className="grid grid-cols-3 gap-1.5">
                    {byDay[g].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => onOpen(m.photo_url ? { kind: "photo", url: m.photo_url } : { kind: "video", url: m.video_url })}
                        className="relative aspect-square rounded-lg overflow-hidden bg-slate-100"
                        data-testid={`gallery-item-${m.id}`}
                      >
                        <img src={m.photo_url || m.video_url.replace(/\.(mp4|mov|m4v|webm)(\?|$)/i, ".jpg$2")} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
                        {m.video_url && (
                          <span className="absolute inset-0 flex items-center justify-center"><span className="w-8 h-8 rounded-full bg-black/45 flex items-center justify-center"><Play className="w-4 h-4" style={{ color: "#fff" }} /></span></span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {byDay[g].map((m) => {
                      const fm = fileMetaOf(m.file_name, m.file_type);
                      return (
                        <button
                          key={m.id}
                          onClick={() => onOpen({ kind: "file", url: m.file_url, name: m.file_name, ftype: m.file_type })}
                          className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 text-left border border-slate-100"
                          data-testid={`gallery-item-${m.id}`}
                        >
                          <span className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: fm.color }}><FileText className="w-5 h-5" style={{ color: "#fff" }} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-slate-900 truncate">{m.file_name || "Fil"}</span>
                            <span className="block text-[11px] text-slate-500">{fm.label} · {fmtBytes(m.file_size)} · {m.sender_name}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function isOnline(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 90 * 1000;
}

// Language helpers used by pure formatter functions below. We can't call
// `useLang()` from non-component code, so we read directly from
// localStorage (kept in sync by LanguageProvider).
const _LANG_KEY = "DOSERNE_LANG";
function _localeFor(lang) {
  return lang === "ro" ? "ro-RO" : lang === "en" ? "en-GB" : "no-NO";
}
function _justNowFor(lang) {
  return lang === "ro" ? "acum" : lang === "en" ? "now" : "nå";
}
function _currentLang() {
  try { return localStorage.getItem(_LANG_KEY) || "no"; } catch { return "no"; }
}

export function timeAgo(iso) {
  if (!iso) return "";
  const lang = _currentLang();
  const d = new Date(iso), s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return _justNowFor(lang);
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}${lang === "ro" ? "h" : "t"}`;
  if (s < 604800) return `${Math.floor(s / 86400)}${lang === "ro" ? "z" : "d"}`;
  return d.toLocaleDateString(_localeFor(lang), { day: "2-digit", month: "short" });
}

export function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(_localeFor(_currentLang()), { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export function otherMember(room, myEmail) {
  if (room.type !== "dm") return "";
  return room.member_emails.find((e) => e.toLowerCase() !== myEmail.toLowerCase()) || "";
}

// -----------------------------------------------------------------
// AUDIO BUBBLE (voice notes)
// -----------------------------------------------------------------
function fmtSecs(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function AudioBubble({ src, durationMs, mine }) {
  const { t } = useLang();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState((durationMs || 0) / 1000);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onEnd = () => { setPlaying(false); setCur(0); a.currentTime = 0; };
    const onMeta = () => {
      // Cloudinary sometimes reports Infinity — fall back to server duration.
      if (isFinite(a.duration) && a.duration > 0) setDur(a.duration);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("loadedmetadata", onMeta);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("loadedmetadata", onMeta);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;
  const remaining = Math.max(0, dur - cur);

  return (
    <div className={`flex items-center gap-2 min-w-[180px] ${mine ? "text-white" : "text-slate-800"}`}>
      <button
        type="button"
        onClick={toggle}
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${mine ? "bg-white/25 hover:bg-white/35" : "bg-slate-200 hover:bg-slate-300"}`}
        style={mine ? { color: "#fff" } : {}}
        data-testid="audio-play-btn"
        aria-label={playing ? t("chat.audio.pause") : t("chat.audio.play")}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-[100px]">
        <div className={`h-1.5 rounded-full ${mine ? "bg-white/30" : "bg-slate-300"} overflow-hidden`}>
          <div className={`h-full ${mine ? "bg-white" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
        </div>
        <div className={`text-[10px] mt-0.5 ${mine ? "text-emerald-100" : "text-slate-500"}`}>
          🎙️ {fmtSecs(playing ? cur : remaining)}
        </div>
      </div>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}

// -----------------------------------------------------------------
// NEW ROOM MODAL
// -----------------------------------------------------------------
export function NewRoomModal({ onClose, authHeader, colleagues, myEmail, onCreated }) {
  const { t } = useLang();
  const [mode, setMode] = useState("dm");
  const [target, setTarget] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const filtered = colleagues.filter((c) =>
    c.email.toLowerCase() !== myEmail.toLowerCase() &&
    (!q || (c.full_name || c.email).toLowerCase().includes(q.toLowerCase()))
  );

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "dm") {
        if (!target) { toast.warning(t("chat.newroom.select_colleague")); setBusy(false); return; }
        const { data } = await axios.post(`${API}/doserne/chat/rooms/dm`, { target_email: target }, authHeader());
        onCreated?.(data); onClose();
      } else {
        if (!groupName.trim() || selected.size === 0) {
          toast.warning(t("chat.newroom.fill_group"));
          setBusy(false); return;
        }
        const { data } = await axios.post(`${API}/doserne/chat/rooms/group`, {
          name: groupName.trim(), member_emails: Array.from(selected)
        }, authHeader());
        onCreated?.(data); onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("chat.toast.failed"));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="chat-new-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-black">{t("fab.new_conversation")}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            <button onClick={() => setMode("dm")} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${mode === "dm" ? "bg-white shadow-sm" : "text-slate-500"}`} data-testid="chat-mode-dm">{t("chat.newroom.tab_dm")}</button>
            <button onClick={() => setMode("group")} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${mode === "group" ? "bg-white shadow-sm" : "text-slate-500"}`} data-testid="chat-mode-group">{t("chat.newroom.tab_group")}</button>
          </div>

          {mode === "group" && (
            <input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t("chat.newroom.group_name_placeholder")}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="chat-group-name"
            />
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("chat.newroom.search_colleague")} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="chat-search-colleague" />
          </div>

          <div className="border border-slate-200 rounded-lg divide-y max-h-72 overflow-y-auto">
            {filtered.map((c) => {
              const isSelected = mode === "dm" ? target === c.email : selected.has(c.email);
              return (
                <button
                  key={c.email}
                  onClick={() => {
                    if (mode === "dm") setTarget(c.email);
                    else setSelected((cur) => {
                      const s = new Set(cur);
                      if (s.has(c.email)) s.delete(c.email); else s.add(c.email);
                      return s;
                    });
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 ${isSelected ? "bg-emerald-50" : ""}`}
                  data-testid={`chat-select-${c.email}`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-black">
                    {(c.full_name || c.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{c.full_name || c.email}</div>
                    <div className="text-[10px] text-slate-500 truncate">{c.email}</div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                </button>
              );
            })}
            {filtered.length === 0 && <div className="p-4 text-xs text-slate-400 italic text-center">Ingen treff</div>}
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold">{t("common.cancel")}</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-black" style={{ color: "#fff" }} data-testid="chat-create-room-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            {t("chat.newroom.create_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// MARK-TO-PROJECT MODAL
// -----------------------------------------------------------------
export function MarkToProjectModal({ messageId, photoUrl, onClose, onDone, authHeader }) {
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/underlag/projects`).then(({ data }) => setProjects(data.projects || []));
  }, []);

  const filtered = useMemo(() => {
    if (!q) return projects.slice(0, 30);
    const s = q.toLowerCase();
    return projects.filter((p) =>
      (p.address || "").toLowerCase().includes(s) ||
      (p.customer || "").toLowerCase().includes(s)
    ).slice(0, 30);
  }, [projects, q]);

  const submit = async (proj) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/doserne/chat/messages/${messageId}/mark-to-project`,
        { project_id: proj.id, note },
        authHeader()
      );
      toast.success(`Bildet lagt til Progresjon på ${data.project_name}`);
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("chat.toast.failed"));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="mark-project-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">{t("chat.action.mark_project")}</div>
            <h2 className="text-xl font-black">{t("chat.marker.title")}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          {photoUrl && <img src={photoUrl} alt="preview" className="w-20 h-20 rounded-lg object-cover mb-3" />}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("chat.reply.replying_to")} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3" data-testid="mark-note-input" />
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("chat.marker.search")} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="mark-search-project" />
          </div>
          <div className="border border-slate-200 rounded-lg divide-y max-h-72 overflow-y-auto">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => submit(p)}
                disabled={busy}
                className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 disabled:opacity-50"
                data-testid={`mark-project-${p.id}`}
              >
                <div className="text-sm font-semibold text-slate-900">{p.address}</div>
                {p.customer && <div className="text-xs text-slate-500 truncate">{p.customer}</div>}
              </button>
            ))}
            {filtered.length === 0 && <div className="p-4 text-xs text-slate-400 italic text-center">{t("chat.marker.no_projects")}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// MESSAGE BUBBLE
// -----------------------------------------------------------------
export function MessageBubble({ msg, mine, myEmail, onReply, onMarkPhoto, onDelete, onReact, onOpenMedia, roomMessages }) {
  const { t } = useLang();
  const [menu, setMenu] = useState(false);
  const menuBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null); // { top, left, direction }
  const replyTo = msg.reply_to ? roomMessages.find((m) => m.id === msg.reply_to) : null;
  const reactions = msg.reactions || {};
  const hasAnyReaction = Object.keys(reactions).length > 0;

  // Compute popover position on open + on resize/scroll so it always stays visible.
  const POPOVER_W = 220;
  const POPOVER_H = 220; // approx max height
  const computePos = useCallback(() => {
    const btn = menuBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceAbove = r.top;
    const spaceBelow = vh - r.bottom;
    // Prefer opening upwards if bubble has more room above, else downwards
    const openUp = spaceAbove > spaceBelow;
    const top = openUp
      ? Math.max(8, r.top - POPOVER_H - 6)
      : Math.min(vh - POPOVER_H - 8, r.bottom + 6);
    // Align horizontally so it stays within viewport
    let left = mine ? r.right - POPOVER_W : r.left;
    left = Math.max(8, Math.min(left, vw - POPOVER_W - 8));
    setMenuPos({ top, left, openUp });
  }, [mine]);

  useEffect(() => {
    if (!menu) return;
    computePos();
    const onScrollOrResize = () => computePos();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [menu, computePos]);

  const openMenu = () => setMenu(true);

  const bubble = (
    <div
      className={`group relative max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-900"} ${msg.deleted ? "opacity-50 italic" : ""}`}
      style={mine ? { color: "#fff" } : {}}
      onContextMenu={(e) => { e.preventDefault(); openMenu(); }}
      data-testid={`chat-msg-${msg.id}`}
    >
      {replyTo && (
        <div className={`text-[10px] mb-1 px-2 py-1 rounded ${mine ? "bg-emerald-700" : "bg-slate-100"} border-l-2 ${mine ? "border-emerald-300" : "border-slate-400"}`}>
          <div className="font-black opacity-80">↩ {replyTo.sender_name}</div>
          <div className="truncate opacity-90">{replyTo.body || (replyTo.photo_url ? "📷 Bilde" : "…")}</div>
        </div>
      )}
      {!mine && msg.sender_name && (
        <div className="text-[10px] font-black text-emerald-700 mb-0.5">{msg.sender_name}</div>
      )}
      {msg.photo_url && (
        <img
          src={msg.photo_url}
          alt={t("chat.message.photo_alt")}
          className="rounded-lg max-w-full mb-1 cursor-pointer max-h-64 object-contain bg-slate-50"
          onClick={() => onOpenMedia?.({ kind: "photo", url: msg.photo_url })}
        />
      )}
      {msg.video_url && (
        <div
          className="relative rounded-lg mb-1 cursor-pointer overflow-hidden bg-slate-900 w-56 h-40"
          onClick={() => onOpenMedia?.({ kind: "video", url: msg.video_url })}
          data-testid={`chat-video-${msg.id}`}
        >
          <img src={msg.video_url.replace(/\.(mp4|mov|m4v|webm)(\?|$)/i, ".jpg$2")} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/25 flex items-center justify-center"><Play className="w-7 h-7" style={{ color: "#fff" }} /></div>
          </div>
        </div>
      )}
      {msg.file_url && (() => {
        const fm = fileMetaOf(msg.file_name, msg.file_type);
        return (
          <button
            onClick={() => onOpenMedia?.({ kind: "file", url: msg.file_url, name: msg.file_name, ftype: msg.file_type })}
            className={`flex items-center gap-2 mb-1 rounded-lg px-2 py-2 w-56 text-left ${mine ? "bg-emerald-700/60 hover:bg-emerald-700" : "bg-slate-100 hover:bg-slate-200"}`}
            data-testid={`chat-file-${msg.id}`}
          >
            <span className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: fm.color }}>
              <FileText className="w-5 h-5" style={{ color: "#fff" }} />
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block text-xs font-bold truncate ${mine ? "text-white" : "text-slate-900"}`}>{msg.file_name || "Fil"}</span>
              <span className={`block text-[10px] ${mine ? "text-emerald-100" : "text-slate-500"}`}>{fm.label} · {fmtBytes(msg.file_size)}</span>
            </span>
          </button>
        );
      })()}
      {msg.audio_url && !msg.deleted && (
        <div className="mb-1">
          <AudioBubble src={msg.audio_url} durationMs={msg.audio_duration_ms} mine={mine} />
        </div>
      )}
      {msg.body && <div className="text-sm whitespace-pre-wrap break-words">{msg.deleted ? t("chat.message.deleted") : msg.body}</div>}
      {msg.marked_project_name && (
        <div className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-black px-1.5 py-0.5 rounded-full ${mine ? "bg-emerald-800" : "bg-amber-100 text-amber-800 border border-amber-200"}`}>
          <Tag className="w-2.5 h-2.5" /> {msg.marked_project_name}
        </div>
      )}
      <div className={`text-[10px] mt-0.5 ${mine ? "text-emerald-100" : "text-slate-400"}`}>{fmtTime(msg.created_at)}</div>

      {hasAnyReaction && (
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(reactions).map(([emoji, users]) => {
            const iReacted = (users || []).some((e) => e.toLowerCase() === myEmail.toLowerCase());
            return (
              <button
                key={emoji}
                onClick={(e) => { e.stopPropagation(); onReact(msg, emoji); }}
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] border ${iReacted ? "bg-white/95 border-white text-slate-900 font-black" : mine ? "bg-emerald-700 border-emerald-500 text-white" : "bg-slate-100 border-slate-200 text-slate-700"}`}
                data-testid={`reaction-${msg.id}-${emoji}`}
                style={iReacted ? {} : mine ? { color: "#fff" } : {}}
              >
                {emoji} <span className="font-bold">{(users || []).length}</span>
              </button>
            );
          })}
        </div>
      )}

      <button
        ref={menuBtnRef}
        onClick={openMenu}
        className={`sm:opacity-0 sm:group-hover:opacity-100 opacity-70 absolute -top-2 ${mine ? "-left-2" : "-right-2"} w-6 h-6 rounded-full bg-white shadow-md border border-slate-200 text-slate-500 hover:text-slate-900 flex items-center justify-center transition z-10`}
        data-testid={`chat-msg-menu-${msg.id}`}
      >
        <MoreVertical className="w-3 h-3" />
      </button>

      {menu && menuPos && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setMenu(false)} data-testid={`msg-menu-backdrop-${msg.id}`} />
          {/* Popover uses fixed positioning via portal so it escapes the
              ChatFab window's `overflow-hidden`. Direction (up/down) is
              chosen dynamically based on available viewport space. */}
          <div
            className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-slate-200 py-1 max-h-[70vh] overflow-y-auto"
            style={{ top: menuPos.top, left: menuPos.left, width: POPOVER_W }}
            data-testid={`msg-menu-portal-${msg.id}`}
          >
            <div className="px-2 py-1 flex items-center justify-around gap-0.5 border-b border-slate-100">
              {EMOJI_SET.map((e) => (
                <button
                  key={e}
                  onClick={() => { setMenu(false); onReact(msg, e); }}
                  className="text-lg hover:scale-125 transition-transform"
                  data-testid={`react-picker-${msg.id}-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <button onClick={() => { setMenu(false); onReply(msg); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-900" data-testid={`msg-action-reply-${msg.id}`}>
              <Reply className="w-3.5 h-3.5 text-slate-500" /> {t("chat.action.reply")}
            </button>
            {msg.photo_url && !msg.marked_project_name && (
              <button onClick={() => { setMenu(false); onMarkPhoto(msg); }} className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 flex items-center gap-2 text-slate-900" data-testid={`msg-action-mark-${msg.id}`}>
                <Tag className="w-3.5 h-3.5 text-amber-600" /> {t("chat.action.mark_project")}
              </button>
            )}
            {mine && !msg.deleted && (
              <button onClick={() => { setMenu(false); onDelete(msg); }} className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid={`msg-action-delete-${msg.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> {t("chat.action.delete")}
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-2`}>
      {bubble}
    </div>
  );
}

// -----------------------------------------------------------------
// ROOM VIEW (messages + input)
// -----------------------------------------------------------------
// `compact` renders a tighter header for use inside a floating chat window.
// `headerLeft` / `headerRight` allow the parent (e.g. ChatFab window frame)
// to inject its own controls (minimize/close/expand) alongside the title.
export function RoomView({
  room, myEmail, authHeader, onBack, colleagueLabels = {},
  compact = false, headerLeft, headerRight,
  onMuteToggled, initialFocusMessageId,
  otherRooms = [], onSwitchRoom,
}) {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [reads, setReads] = useState({});  // {email: last_read_at}
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [reply, setReply] = useState(null);
  const [markTarget, setMarkTarget] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [muted, setMuted] = useState(!!room.muted);
  // When a photo is picked, park it here so the user can annotate it
  // in the PhotoEditor modal BEFORE we upload.
  const [pendingPhoto, setPendingPhoto] = useState(null);
  // Call state: "audio" | "video" | null (also handles overlay dismiss).
  const [startCall, setStartCall] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const docRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { setMuted(!!room.muted); }, [room.muted, room.id]);

  const roomLabel = room.type === "dm"
    ? (colleagueLabels[otherMember(room, myEmail)] || otherMember(room, myEmail))
    : room.name || (room.type === "project" ? t("chat.header.project") : t("chat.header.group"));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/chat/rooms/${room.id}/messages`, authHeader());
      setMessages(data.messages || []);
      setReads(data.reads || {});
    } catch (_e) { /* ignore */ void 0; }
    setLoading(false);
  }, [room.id, authHeader]);

  const poll = useCallback(async () => {
    setMessages((cur) => {
      const last = cur[cur.length - 1];
      const since = last?.created_at;
      if (!since) return cur;
      axios.get(`${API}/doserne/chat/rooms/${room.id}/messages?since=${encodeURIComponent(since)}`, authHeader())
        .then(({ data }) => {
          if (data.reads) setReads(data.reads);
          if (data.messages && data.messages.length > 0) {
            setMessages((prev) => {
              const seen = new Set(prev.map((m) => m.id));
              return [...prev, ...data.messages.filter((m) => !seen.has(m.id))];
            });
          }
        })
        .catch(() => {});
      return cur;
    });
  }, [room.id, authHeader]);

  useEffect(() => {
    setLoading(true);
    load();
    axios.post(`${API}/doserne/chat/rooms/${room.id}/read`, {}, authHeader()).catch(() => {});
    const iv = setInterval(() => { poll(); }, POLL_MS);
    return () => clearInterval(iv);
  }, [room.id, load, poll, authHeader]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (extra = {}) => {
    const body = (extra.body_override ?? text).trim();
    const hasPhoto = !!extra.photo_url;
    const hasAudio = !!extra.audio_url;
    const hasFile = !!extra.file_url;
    if (!body && !hasPhoto && !hasAudio && !hasFile) return;
    setSending(true);
    try {
      const payload = {
        body,
        photo_url: extra.photo_url || "",
        audio_url: extra.audio_url || "",
        audio_duration_ms: extra.audio_duration_ms || 0,
        file_url: extra.file_url || "",
        file_name: extra.file_name || "",
        file_size: extra.file_size || 0,
        file_type: extra.file_type || "",
        reply_to: reply?.id || null,
      };
      const { data } = await axios.post(`${API}/doserne/chat/rooms/${room.id}/messages`, payload, authHeader());
      setMessages((cur) => [...cur, data]);
      if (!extra.body_override) setText("");
      setReply(null);
      setShowEmoji(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("chat.toast.send_failed"));
    }
    setSending(false);
  };

  const uploadAndSendPhoto = useCallback(async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/doserne/chat/upload-photo`, form, {
        ...authHeader(),
        headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      await send({ photo_url: data.url });
    } catch (_er) {
      toast.error(t("chat.toast.upload_failed"));
    }
    setUploadingPhoto(false);
  }, [authHeader, t]);

  const uploadAndSendFile = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t("chat.toast.file_too_large") === "chat.toast.file_too_large" ? "Filen er for stor (maks 100MB)" : t("chat.toast.file_too_large"));
      return;
    }
    setUploadingFile(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/doserne/chat/upload-file`, form, {
        ...authHeader(),
        headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      await send({ file_url: data.url, file_name: data.file_name || file.name, file_size: data.file_size || file.size, file_type: data.file_type || file.type });
    } catch (_er) {
      toast.error(t("chat.toast.upload_failed"));
    }
    setUploadingFile(false);
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) await uploadAndSendFile(f);
  };


  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // Park the file — the PhotoEditor decides whether to upload as-is
    // or with annotations.
    setPendingPhoto(f);
  };

  // Ctrl+V / Cmd+V paste an image → open the PhotoEditor (same flow
  // as picking from gallery/camera). Works anywhere in the room view.
  useEffect(() => {
    const onPaste = (ev) => {
      const items = ev.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const file = it.getAsFile();
          if (file) {
            ev.preventDefault();
            const ext = (file.type.split("/")[1] || "png").split("+")[0];
            const named = new File([file], `pasted-${Date.now()}.${ext}`, { type: file.type });
            setPendingPhoto(named);
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const onVoiceDone = async (blob, durationMs) => {
    setUploadingAudio(true);
    try {
      const form = new FormData();
      form.append("file", new File([blob], "voice.webm", { type: blob.type || "audio/webm" }));
      const { data } = await axios.post(`${API}/doserne/chat/upload-audio`, form, {
        ...authHeader(),
        headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      await send({
        audio_url: data.url,
        audio_duration_ms: data.duration_ms || durationMs,
      });
    } catch (e) {
      toast.error(t("chat.toast.voice_failed"));
    }
    setUploadingAudio(false);
  };

  const voice = useVoiceRecorder({
    onDone: onVoiceDone,
    onError: (m) => toast.error(m),
  });

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current;
    if (!ta) { setText((t) => t + emoji); return; }
    const start = ta.selectionStart ?? text.length;
    const end = ta.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    // Restore caret after React re-render.
    requestAnimationFrame(() => {
      try {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
      } catch { /* ignore */ }
    });
  };

  const toggleMute = async () => {
    try {
      const { data } = await axios.post(`${API}/doserne/chat/rooms/${room.id}/mute`, {}, authHeader());
      setMuted(!!data.muted);
      onMuteToggled?.(room.id, !!data.muted);
      toast.success(data.muted ? t("chat.toast.muted_on") : t("chat.toast.muted_off"));
    } catch {
      toast.error(t("chat.toast.failed"));
    }
  };

  const deleteMsg = async (m) => {
    if (!window.confirm(t("chat.confirm.delete_message"))) return;
    try {
      await axios.delete(`${API}/doserne/chat/messages/${m.id}`, authHeader());
      setMessages((cur) => cur.map((x) => x.id === m.id ? { ...x, deleted: true, body: "", photo_url: "" } : x));
    } catch (e) {
      toast.error(t("chat.toast.failed"));
    }
  };

  const reactMsg = async (m, emoji) => {
    try {
      const { data } = await axios.post(
        `${API}/doserne/chat/messages/${m.id}/react`,
        { emoji },
        authHeader()
      );
      setMessages((cur) => cur.map((x) => x.id === m.id ? { ...x, reactions: data.reactions } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("chat.toast.failed"));
    }
  };

  const online = room.type === "dm" && isOnline(room.other_last_seen_at);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {compact ? (
        <div className="px-3 py-2 border-b border-slate-200 bg-gradient-to-r from-[#0084ff] to-[#0064d1] flex items-center gap-2" style={{ color: "#fff" }}>
          {headerLeft}
          <div className={`relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${room.type === "group" ? "bg-amber-200/90 text-amber-800" : room.type === "project" ? "bg-cyan-200/90 text-cyan-800" : "bg-white/95 text-slate-700"}`}>
            {room.type === "group" || room.type === "project" ? <Users className="w-4 h-4" /> : (roomLabel || "?").slice(0, 1).toUpperCase()}
            {online && (
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0084ff]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black truncate leading-tight flex items-center gap-1" style={{ color: "#fff" }}>
              {roomLabel}
              {muted && <BellOff className="w-3 h-3 opacity-80" />}
            </div>
            <div className="text-[10px] leading-tight opacity-90">
              {room.type === "group" ? t("chat.header.members_count", { count: room.member_emails.length }) :
               room.type === "project" ? `${t("chat.header.project")} · ${room.member_emails.length}` :
               online ? t("chat.header.active_now") : t("chat.header.dm")}
            </div>
          </div>
          <button
            onClick={() => setStartCall("audio")}
            className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center flex-shrink-0"
            style={{ color: "#fff" }}
            title="Ring"
            data-testid={`chat-call-audio-${room.id}`}
          >
            <Phone className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setStartCall("video")}
            className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center flex-shrink-0"
            style={{ color: "#fff" }}
            title="Videosamtale"
            data-testid={`chat-call-video-${room.id}`}
          >
            <Video className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={toggleMute}
            className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center flex-shrink-0"
            style={{ color: "#fff" }}
            title={muted ? t("chat.header.unmute") : t("chat.header.mute")}
            data-testid={`chat-mute-btn-${room.id}`}
          >
            {muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
          </button>
          {headerRight}
        </div>
      ) : (
        <div className="px-3 py-2.5 border-b border-slate-200 bg-white flex items-center gap-2">
          <button onClick={onBack} className="lg:hidden w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="chat-back-btn"><ArrowLeft className="w-4 h-4" /></button>
          <div className={`relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${room.type === "group" ? "bg-amber-100 text-amber-700" : room.type === "project" ? "bg-cyan-100 text-cyan-700" : "bg-slate-200 text-slate-600"}`}>
            {room.type === "group" || room.type === "project" ? <Users className="w-4 h-4" /> : (roomLabel || "?").slice(0, 1).toUpperCase()}
            {online && (
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black text-slate-900 truncate flex items-center gap-1">
              {roomLabel}
              {muted && <BellOff className="w-3.5 h-3.5 text-slate-400" />}
            </div>
            <div className="text-[10px] text-slate-400">
              {room.type === "group" ? t("chat.header.members_count", { count: room.member_emails.length }) :
               room.type === "project" ? `${t("chat.header.project")} · ${room.member_emails.length}` :
               online ? t("chat.header.active_now") : t("chat.header.dm")}
            </div>
          </div>
          <button
            onClick={() => setStartCall("audio")}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center flex-shrink-0"
            title="Ring"
            data-testid={`chat-call-audio-desk-${room.id}`}
          >
            <Phone className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={() => setStartCall("video")}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center flex-shrink-0"
            title="Videosamtale"
            data-testid={`chat-call-video-desk-${room.id}`}
          >
            <Video className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={toggleMute}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center flex-shrink-0"
            title={muted ? t("chat.header.unmute") : t("chat.header.mute")}
            data-testid={`chat-mute-btn-${room.id}`}
          >
            {muted ? <BellOff className="w-4 h-4 text-slate-400" /> : <Bell className="w-4 h-4 text-slate-500" />}
          </button>
        </div>
      )}

      {/* Room switcher pills — quick swap between recent conversations
          without closing the overlay. Parent passes `otherRooms` and
          `onSwitchRoom`. Hidden if no other rooms exist. */}
      {onSwitchRoom && otherRooms.length > 0 && (
        <div className="px-2 py-1.5 border-b border-slate-200 bg-slate-50/70 flex-shrink-0" data-testid="chat-room-switcher">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: "none" }}>
            {otherRooms.slice(0, 20).map((r) => {
              const other = otherMember(r, myEmail);
              const label = r.type === "dm"
                ? (colleagueLabels[other] || other)
                : (r.name || (r.type === "project" ? t("chat.header.project") : t("chat.header.group")));
              const unread = r.unread || 0;
              const isDot = isOnline(r.other_last_seen_at) && r.type === "dm";
              return (
                <button
                  key={r.id}
                  onClick={() => onSwitchRoom(r)}
                  className="relative flex-shrink-0 flex flex-col items-center w-12 group"
                  data-testid={`chat-switch-${r.id}`}
                  title={label}
                >
                  <div className="relative">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black shadow-sm border-2 border-white ${r.type === "group" ? "bg-amber-200 text-amber-800" : r.type === "project" ? "bg-cyan-200 text-cyan-800" : "bg-gradient-to-br from-emerald-400 to-cyan-500 text-white"}`}>
                      {r.type === "group" || r.type === "project"
                        ? <Users className="w-4 h-4" />
                        : (label || "?").slice(0, 1).toUpperCase()}
                    </div>
                    {isDot && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                    )}
                    {unread > 0 && !r.muted && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 border border-white text-[9px] font-black flex items-center justify-center"
                        style={{ color: "#fff" }}
                      >
                        {unread > 9 ? "9+" : unread}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[9px] font-semibold text-slate-600 truncate w-full text-center leading-tight">
                    {(label || "").split(" ")[0]}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-3">
        {/* Trick: `min-h-full flex flex-col justify-end` keeps the
            latest messages anchored to the bottom of the scroll pane
            even when there are only a handful of them. Once messages
            grow past the pane height, the wrapper expands naturally
            and normal scrolling kicks in. */}
        <div className="min-h-full flex flex-col justify-end">
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-xs text-slate-400 italic mt-10">Ingen meldinger ennå. Skriv en hilsen!</div>
        ) : (() => {
          // For each other member, find the newest MY message they've
          // read (msg.created_at <= their last_read_at) so we can pin
          // their avatar under it as a Messenger-style read receipt.
          const readersByMsgId = {};
          const others = (room.member_emails || []).filter(
            (e) => e.toLowerCase() !== myEmail.toLowerCase()
          );
          for (const otherEmail of others) {
            const lastRead = reads[otherEmail];
            if (!lastRead) continue;
            // Iterate messages backwards; pick newest of MINE that is <= lastRead.
            for (let i = messages.length - 1; i >= 0; i--) {
              const m = messages[i];
              if (m.sender_email.toLowerCase() !== myEmail.toLowerCase()) continue;
              if (m.created_at <= lastRead) {
                if (!readersByMsgId[m.id]) readersByMsgId[m.id] = [];
                readersByMsgId[m.id].push(otherEmail);
                break;
              }
            }
          }
          return messages.map((m) => (
            <React.Fragment key={m.id}>
              <MessageBubble
                msg={m}
                mine={m.sender_email.toLowerCase() === myEmail.toLowerCase()}
                myEmail={myEmail}
                onReply={setReply}
                onMarkPhoto={(msg) => setMarkTarget(msg)}
                onDelete={deleteMsg}
                onReact={reactMsg}
                onOpenMedia={setViewer}
                roomMessages={messages}
              />
              {readersByMsgId[m.id] && (
                <div className="flex justify-end -mt-1 mb-1.5 pr-1">
                  <div className="flex items-center gap-0.5" data-testid={`read-receipts-${m.id}`}>
                    {readersByMsgId[m.id].slice(0, 5).map((email) => {
                      const name = colleagueLabels[email] || email;
                      return (
                        <div
                          key={email}
                          className="w-4 h-4 rounded-full bg-slate-700 flex items-center justify-center text-[8px] font-black shadow-sm border border-white"
                          style={{ color: "#fff" }}
                          title={`Sett av ${name}`}
                        >
                          {(name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          ));
        })()}
        </div>
      </div>

      {reply && (
        <div className="px-3 py-1.5 bg-emerald-50 border-t border-emerald-200 flex items-center gap-2">
          <CornerDownRight className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black text-emerald-800">Svarer {reply.sender_name}</div>
            <div className="text-xs text-emerald-700 truncate">{reply.body || (reply.photo_url ? "📷 Bilde" : reply.video_url ? "🎬 Video" : reply.file_url ? "📎 Fil" : "")}</div>
          </div>
          <button onClick={() => setReply(null)} className="w-6 h-6 rounded hover:bg-emerald-100 flex items-center justify-center"><X className="w-3 h-3 text-emerald-700" /></button>
        </div>
      )}

      <div
        className={`${compact ? "p-2" : "p-3"} border-t border-slate-200 bg-white relative`}
        style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${compact ? "8px" : "12px"})` }}
      >
        {showEmoji && (
          <div className="absolute bottom-full right-2 mb-2 z-50 shadow-2xl rounded-xl overflow-hidden border border-slate-200 bg-white">
            <Suspense fallback={<div className="w-[300px] h-[360px] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}>
              <EmojiPicker
                onEmojiClick={(e) => insertEmoji(e.emoji)}
                emojiStyle="native"
                theme="light"
                width={compact ? 300 : 340}
                height={compact ? 360 : 400}
                searchDisabled={false}
                previewConfig={{ showPreview: false }}
                lazyLoadEmojis
              />
            </Suspense>
          </div>
        )}
        {voice.recording && (
          <div className="absolute inset-0 z-40 bg-red-50 border-2 border-red-300 border-dashed rounded-lg flex items-center justify-between px-4">
            <div className="flex items-center gap-2 text-red-700 font-black">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm">Tar opp… {fmtSecs(voice.seconds)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={voice.cancel}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-red-200 text-red-600 text-xs font-bold"
                data-testid="voice-cancel-btn"
              >
                <Trash className="w-3.5 h-3.5" /> {t("common.cancel")}
              </button>
              <button
                onClick={voice.stop}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500 hover:bg-red-600 text-xs font-black"
                style={{ color: "#fff" }}
                data-testid="voice-stop-btn"
              >
                <Send className="w-3.5 h-3.5" /> {t("chat.composer.send")}
              </button>
            </div>
          </div>
        )}
        <div className="flex items-end gap-1.5">
          {/* Gallery picker */}
          <input type="file" accept="image/*" ref={fileRef} onChange={onPhotoPick} className="hidden" data-testid="chat-photo-input" tabIndex={-1} aria-hidden="true" />
          {/* Camera capture — opens the native camera on mobile via
              `capture="environment"` (rear camera). Same upload path
              as the gallery picker. */}
          <input type="file" accept="image/*" capture="environment" ref={cameraRef} onChange={onPhotoPick} className="hidden" data-testid="chat-camera-input" tabIndex={-1} aria-hidden="true" />
          <input type="file" ref={docRef} onChange={onFilePick} className="hidden" data-testid="chat-file-input" tabIndex={-1} aria-hidden="true" />
          {/* Single "+" button — opens a menu with every attachment option,
              keeping the composer clean so the text field stays wide. */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => { setShowAttach((v) => !v); setShowEmoji(false); }}
              disabled={voice.recording}
              className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-lg ${showAttach ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600"} disabled:opacity-40 flex items-center justify-center flex-shrink-0 transition`}
              data-testid="chat-attach-btn"
              title="Legg ved"
              aria-label="Legg ved"
            >
              {(uploadingPhoto || uploadingFile) ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className={`w-5 h-5 transition-transform ${showAttach ? "rotate-45" : ""}`} />
              )}
            </button>
            {showAttach && (
              <>
                <div className="fixed inset-0 z-[40]" onClick={() => setShowAttach(false)} data-testid="chat-attach-backdrop" />
                <div className="absolute bottom-full left-0 mb-2 z-[50] bg-white rounded-2xl shadow-xl border border-slate-200 p-1.5 w-56" data-testid="chat-attach-menu">
                  {[
                    { key: "camera", label: "Ta bilde", Icon: Camera, cls: "bg-rose-100 text-rose-600", act: () => cameraRef.current?.click() },
                    { key: "photo", label: "Bilde fra galleri", Icon: ImageIcon, cls: "bg-emerald-100 text-emerald-600", act: () => fileRef.current?.click() },
                    { key: "file", label: "Send fil", Icon: Paperclip, cls: "bg-sky-100 text-sky-600", act: () => docRef.current?.click() },
                    { key: "voice", label: "Talemelding", Icon: Mic, cls: "bg-orange-100 text-orange-600", act: () => voice.start() },
                    { key: "emoji", label: "Emoji", Icon: Smile, cls: "bg-amber-100 text-amber-600", act: () => setShowEmoji(true) },
                    { key: "media", label: "Media og filer", Icon: Images, cls: "bg-violet-100 text-violet-600", act: () => setShowGallery(true) },
                  ].map(({ key, label, Icon, cls, act }) => (
                    <button
                      key={key}
                      onClick={() => { setShowAttach(false); act(); }}
                      className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl hover:bg-slate-100 text-left transition"
                      data-testid={`chat-attach-${key}`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cls}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="text-sm font-semibold text-slate-700">{label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setShowEmoji(false)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t("chat.composer.placeholder")}
            rows={1}
            className={`flex-1 px-3 ${compact ? "py-1.5" : "py-2"} border border-slate-300 rounded-lg text-base sm:text-sm resize-none max-h-32`}
            style={{ fontSize: "16px" }}
            data-testid="chat-input"
            enterKeyHint="send"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="sentences"
            spellCheck={false}
          />
          {text.trim() || uploadingPhoto || uploadingAudio ? (
            <button
              onClick={() => send()}
              disabled={sending || (!text.trim() && !uploadingPhoto && !uploadingAudio)}
              className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-lg bg-[#0084ff] hover:bg-[#0074e1] disabled:opacity-50 flex items-center justify-center flex-shrink-0`}
              style={{ color: "#fff" }}
              data-testid="chat-send-btn"
              title={t("chat.composer.send")}
            >
              {sending || uploadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          ) : (
            <button
              onClick={() => send({ body_override: "👍" })}
              disabled={sending}
              className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-lg bg-[#0084ff] hover:bg-[#0074e1] active:scale-90 disabled:opacity-50 flex items-center justify-center flex-shrink-0 transition-transform`}
              style={{ color: "#fff" }}
              data-testid="chat-like-btn"
              title={t("chat.composer.send_like")}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {markTarget && (
        <MarkToProjectModal
          messageId={markTarget.id}
          photoUrl={markTarget.photo_url}
          onClose={() => setMarkTarget(null)}
          onDone={(res) => {
            setMessages((cur) => cur.map((m) => m.id === markTarget.id ? { ...m, marked_project_id: res?.project_id, marked_project_name: res?.project_name } : m));
          }}
          authHeader={authHeader}
        />
      )}

      {pendingPhoto && (
        <PhotoEditor
          file={pendingPhoto}
          onCancel={() => setPendingPhoto(null)}
          onDone={async (edited) => {
            setPendingPhoto(null);
            await uploadAndSendPhoto(edited);
          }}
          labels={{
            title: t("chat.editor.title") || "Rediger bilde",
            send: t("chat.composer.send") || "Send",
            skip: t("chat.editor.send_original") || "Send uten redigering",
            undo: t("chat.editor.undo") || "Angre",
            clear: t("chat.editor.clear") || "Nullstill",
            cancel: t("common.cancel") || "Avbryt",
          }}
        />
      )}

      {startCall && (
        <Suspense fallback={null}>
          <CallOverlay
            direction="outgoing"
            mode={startCall}
            chatRoomId={room.id}
            peerLabel={roomLabel}
            authHeader={authHeader}
            onClose={() => setStartCall(null)}
          />
        </Suspense>
      )}

      <MediaViewer item={viewer} onClose={() => setViewer(null)} />
      {showGallery && (
        <MediaGallery
          roomId={room.id}
          authHeader={authHeader}
          onClose={() => setShowGallery(false)}
          onOpen={(it) => { setShowGallery(false); setViewer(it); }}
        />
      )}
    </div>
  );
}
